import React from 'react';
import { Player } from '../types';
import { formatPlayerName } from '../utils/playerUtils';
import './PlayerList.css';

interface PlayerListProps {
  players: Player[];
  seasonName?: string;
  currentRound?: number;
  onPlayerClick: (player: Player) => void;
}

const PlayerList: React.FC<PlayerListProps> = ({ players, seasonName = "2025-2026", currentRound, onPlayerClick }) => {

  // Debug: tulostetaan pelaajien data
  console.log('Players data:', players);

  // Calculate previous ranks based on stars up to previous round
  const calculatePreviousRanks = (players: Player[]) => {
    // Find the current round (highest round number among all players)
    const currentRound = Math.max(...players.flatMap(player => 
      Object.keys(player.roundStars || {}).map(Number)
    ));
    const previousRound = currentRound - 1;

    // Calculate total stars up to previous round for each player
    const playersWithPreviousStars = players.map(player => {
      const previousStars = Object.entries(player.roundStars || {})
        .filter(([round]) => Number(round) <= previousRound)
        .reduce((sum, [, stars]) => sum + stars, 0);
      
      return {
        ...player,
        previousStars
      };
    });

    // Sort by previous stars to get previous ranking
    const sortedByPrevious = [...playersWithPreviousStars].sort((a, b) => b.previousStars - a.previousStars);
    
    // Add previous rank to each player
    return playersWithPreviousStars.map(player => {
      const previousRank = sortedByPrevious.findIndex(p => p.name === player.name) + 1;
      return {
        ...player,
        previousRank
      };
    });
  };

  const playersWithPreviousRanks = calculatePreviousRanks(players);


  const getRankChangeIcon = (player: any) => {
    const rankChange = (player.previousRank || 0) > 0 ? (player.previousRank || 0) - (player.currentRank || 0) : 0;
    if (rankChange > 0) return '↗️';
    if (rankChange < 0) return '↘️';
    return '➡️';
  };

  const getRankChangeClass = (player: any) => {
    const rankChange = (player.previousRank || 0) > 0 ? (player.previousRank || 0) - (player.currentRank || 0) : 0;
    if (rankChange > 0) return 'rank-up';
    if (rankChange < 0) return 'rank-down';
    return 'rank-same';
  };

  const getRankChangeAmount = (player: any) => {
    const rankChange = (player.previousRank || 0) > 0 ? (player.previousRank || 0) - (player.currentRank || 0) : 0;
    return Math.abs(rankChange);
  };

  return (
    <div className="player-list-card">
      <div className="player-list-header">
        <h2>Kärppien tähtipelaajat kaudella {seasonName}</h2>
      </div>
      
      <div className="player-list">
        {playersWithPreviousRanks.map((player, index) => (
          <div 
            key={player.name} 
            className={`player-row ${index < 3 ? 'top-three' : ''}`}
            onClick={() => onPlayerClick(player)}
            style={{ cursor: 'pointer' }}
          >
            <div className="rank-section">
              <span className="rank-number">#{player.currentRank || 0}</span>
            </div>
            
            <div className="player-info">
              <div className="player-name">
                {formatPlayerName(player)}
              </div>
              <div className="player-stats">
                <span className="stars-count">{player.totalStars || 0} tähteä</span>
                {player.roundStars && currentRound && player.roundStars[currentRound] !== undefined && (
                  <span className="round-stars">
                    (+{player.roundStars[currentRound]})
                  </span>
                )}
              </div>
            </div>
            
            <div className="rank-change-section">
              {(player.previousRank || 0) > 0 && (
                <span className={`rank-change-indicator ${getRankChangeClass(player)}`}>
                  {getRankChangeIcon(player)} {getRankChangeAmount(player)}
                </span>
              )}
            </div>
            
            <div className="stars-visual">
              {(player.previousRank || 0) > 0 && (
                <span className="rank-change-text">
                  Edellinen: #{player.previousRank}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default PlayerList;
